'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import useSWR from 'swr';
import { useEnvVars } from '@web/app/contexts/EnvContext';
import { useShopifyCartContext } from '../onboarding/OnboardingFlowContainer/ShopifyCartProvider';
import { shared } from '../config';
import { ApiResult } from '../api';
import { OFFER_URL } from './constants';
import { getFormattedOfferAmountByType } from './helpers';
import { ShopifyOfferContext } from './shopifyContext';

const useQueryOfferDetails = () => {
  const searchParams = useSearchParams();
  const [queryOfferCode, setQueryOfferCode] = useState(null);

  useEffect(() => {
    const offerCodeParam = searchParams?.get('offer_code');
    if (offerCodeParam) {
      setQueryOfferCode(offerCodeParam);
      // Persist the offer code for attribution and to bridge the app router
      // -> pages router boundary when the user navigates into onboarding.
      // The cart hook reads this on creation; the latest captured code
      // always wins because this write overwrites on every new URL hit.
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem('offerCode', offerCodeParam);
        } catch (e) {
          // swallow the error
        }
      }
    }
  }, [searchParams]);

  const { data, error } = useSWR(
    queryOfferCode ? `${OFFER_URL}${queryOfferCode}/` : null,
  );

  const [invalidQueryCodes, setInvalidQueryCodes] = useState([]);

  useEffect(() => {
    if (error) {
      setInvalidQueryCodes(prevCodes => [...prevCodes, queryOfferCode]);
      setQueryOfferCode(null);
    }
  }, [error, invalidQueryCodes, queryOfferCode]);

  return {
    queryOfferCode,
    offerDetails: data,
    error,
    invalidQueryCodes,
    isLoading: queryOfferCode ? !error && !data : false,
    setQueryOfferCode,
  };
};

export const AppShopifyOfferProvider = ({ children }) => {
  const appEnvs = useEnvVars();
  const cartContext = useShopifyCartContext();

  const safeCartContext = cartContext || {
    cartId: null,
    error: false,
    couponCode: null,
    isLoading: true,
    addPromo: () => Promise.resolve(null),
  };

  const {
    cartId,
    error: cartError,
    couponCode: cartOfferCode,
    isLoading: isCartLoading,
    addPromo,
  } = safeCartContext;

  const isCartReady =
    (safeCartContext?.cart_id || safeCartContext?.cart?.cart_id) &&
    !cartError &&
    !isCartLoading;

  const {
    queryOfferCode,
    offerDetails: queryOfferDetails,
    isLoading: isQueryLoading,
    setQueryOfferCode,
  } = useQueryOfferDetails();

  const [invalidCodes, setInvalidCodes] = useState([]);

  const defaultOfferCode =
    appEnvs?.DEFAULT_OFFER_CODE ?? shared.DEFAULT_OFFER_CODE;

  const offerCode = useMemo(() => {
    if (isQueryLoading || isCartLoading) {
      return null;
    }
    if (queryOfferDetails) {
      return queryOfferDetails.code || queryOfferCode;
    }
    if (cartOfferCode) {
      return cartOfferCode;
    }
    return defaultOfferCode;
  }, [
    cartOfferCode,
    isQueryLoading,
    isCartLoading,
    queryOfferDetails,
    queryOfferCode,
    defaultOfferCode,
  ]);

  const { data: offerDetails } = useSWR(
    offerCode ? `${OFFER_URL}${offerCode}/` : null,
    { revalidateIfStale: false },
  );

  useEffect(() => {
    if (
      isCartReady &&
      cartId &&
      offerCode &&
      offerCode !== cartOfferCode &&
      !invalidCodes.includes(offerCode)
    ) {
      addPromo(offerCode).then(result => {
        setQueryOfferCode(null);
        ApiResult.ifError(result, () => {
          setInvalidCodes(codes => [...codes, offerCode]);
        });
      });
    }
  }, [
    cartId,
    cartOfferCode,
    invalidCodes,
    offerCode,
    addPromo,
    setQueryOfferCode,
    isCartReady,
  ]);

  const offerState = useMemo(() => {
    const baseState = {
      addPromoCode: addPromo || (() => Promise.resolve(null)),
      formattedAmount: '50%',
      defaultOfferCode: defaultOfferCode || 'DEFAULT',
    };
    if (offerDetails) {
      const formattedAmount =
        getFormattedOfferAmountByType(offerDetails) || '50%';
      return {
        ...baseState,
        ...offerDetails,
        formattedAmount,
        defaultOfferCode: defaultOfferCode || 'DEFAULT',
      };
    }
    return baseState;
  }, [addPromo, offerDetails, defaultOfferCode]);

  return (
    <ShopifyOfferContext.Provider value={offerState}>
      {children}
    </ShopifyOfferContext.Provider>
  );
};
